// Field — THE field wrapper for every entry screen: a label, at most one hint,
// and the box. Item 70 of SWEEP-SCOPE-2026-09-13: this was copied into fourteen
// screens in three shapes (with hint / without hint / optional label); this is
// the one definition, taken from the best copy (Between parties — its hint is
// clipped so a long hint can never push a box below its neighbours, §5.8).
//
// THE RULE IT ENFORCES (§7 rules 3–4): a label says what goes in the box, and
// the hint — 10px, muted, right of the label — carries ONLY a fact the label
// cannot: the unit ("AED per USDT"), "optional", "· auto", "optional · Dubai
// time". A hint never explains, never repeats the label, never repeats the
// placeholder. Anything longer belongs in "About this screen" (ScreenHelp).
//
// Usage:
//   <Field label="Rate" hint="AED per USDT · auto">
//     <input className="input" … />
//   </Field>
//
//   <FieldRow>                       // stacks on the phone, two columns from lg
//     <Field label="Rate" hint="AED per USDT · auto">…</Field>
//     <Field label="When did this happen?" hint="optional · Dubai time">…</Field>
//   </FieldRow>
//   <FieldRow cols={3}>…</FieldRow>  // three columns from lg (the FX / fee trios)

import type { ReactNode } from 'react';

export function Field({ label, hint, children, className = '' }: {
  label: string;
  /** One short fact the label cannot carry. Not a sentence. */
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1 min-w-0 ${className}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-meta text-tg-hint">{label}</span>
        {hint && (
          <span className="text-meta text-tg-hint opacity-70 truncate max-w-[60%]" title={hint}>
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/** A row of fields: one column on the phone, `cols` columns from lg. Items
 *  align to the top so an ErrorLine under one field never stretches the box
 *  beside it (§5.2 — the two movement boxes got unequal heights). */
const COLS = { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' } as const;

export function FieldRow({ children, cols = 2, className = '' }: {
  children: ReactNode;
  cols?: 2 | 3;
  className?: string;
}) {
  return (
    <div className={`grid grid-cols-1 gap-3 lg:items-start ${COLS[cols]} ${className}`}>
      {children}
    </div>
  );
}
